import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton } from "@mui/material";
import Add from "@mui/icons-material/Add";
import ModeEditOutlineTwoTone from "@mui/icons-material/ModeEditOutlineTwoTone";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import TableFilter from "components/Shared/AppTable/TableFilter";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyGetVendorStaffListQuery } from "store/services/vendorStaffApi";
import { CheckCircleOutlineOutlined } from "@mui/icons-material";

const VendorStaffListScreen = () => {
  const breadcrumbs = [{ title: "Staffs" }];
  const { loggedInUser } = useAuth();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();

  const keyword = searchParams.get("search") || "";

  const columns = [
    {
      name: "Name",
      selector: (row) => row?.name,
      cell: (row) => (
        <div title={row?.name} className="line-clamp-2">
          {row?.name ? (
            <HighlightedText text={row.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      maxWidth: "300px",
    },

    {
      name: "Staff ID",
      selector: (row) => row?.vendorStaffId,
      cell: (row) => (
        <Link
          title={row?.vendorStaffId}
          to={`/app/crm/vendor/staffs/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.vendorStaffId ? (
            <HighlightedText text={row?.vendorStaffId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },

    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <div title={row?.email} className="line-clamp-2">
          {row?.email ? row.email : "N/A"}
        </div>
      ),
      maxWidth: "400px",
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => (
        <div title={row?.phone} className="line-clamp-2">
          {row?.phone ? row.phone : "N/A"}
        </div>
      ),
      maxWidth: "250px",
    },


    {
      name: "Actions",
      cell: (row) => (
        <>
          <Box display={"flex"}  >
            {row?.connectedUser?.isActive && (
              <IconButton color="secondary" aria-label="Deactivated">
                <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} />
              </IconButton>
            )}
            <Link to={`/app/vendor/staffs/${row._id}/detail`}>
              <IconButton color="primary" aria-label="Edit Task">
                <InfoOutlined />
              </IconButton>
            </Link>

            <Link to={`/app/vendor/staffs/${row._id}/edit`}>
              <IconButton size="small" aria-label="Edit Task">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          </Box>
        </>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  // RTK Query
  const [fetchStaffs, { data: staffList, isLoading: loadingStaffs, error }] =
    useLazyGetVendorStaffListQuery();

  React.useEffect(() => {
    const filterParams = {
      keyword: keyword,
      page: currentPage,
      limit: perPage,
      vendorId: loggedInUser?.typeRef,
    };
    fetchStaffs(filterParams);
  }, [currentPage, fetchStaffs, keyword, loggedInUser?.typeRef, perPage]);

  useAppMeta("Staffs");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <TableFilter searchLabel="Staffs">
          <div className="font-medium">Staffs</div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={staffList?.data ?? []}
          paginationPerPage={perPage}
          paginationServer
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          paginationTotalRows={staffList?.totalSize ?? 0}

          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={loadingStaffs}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VendorStaffListScreen;

const PageActions = () => {
  return (
    <Link to="/app/vendor/staffs/create">
      <Button startIcon={<Add />} variant="contained">
        New Staff
      </Button>
    </Link>
  );
};
